import { Button } from '@/shadcn-ui/button';
import { createThumbnail } from '../components/pdp/image-preview';
import { motion } from 'motion/react';
import Gallery from '@/icons/gallery';
import { compact, concat } from 'lodash-es';
import { calculateThumbnails } from '../utils/pdp/calculate-thumbnails';

interface UseAssembleThumbnailsProps {
  exteriorImageUrls: string[];
  interiorImageUrls: string[];
  interior360?: string;
  onClickDelegate: (image?: string) => void;
}

export default function useAssembleThumbnails({
  exteriorImageUrls,
  interiorImageUrls,
  interior360,
  onClickDelegate,
}: UseAssembleThumbnailsProps) {
  // We should aim to always show 5 thumbnails total if possible.
  const {
    exteriorThumbnailUrl,
    interiorThumbnailUrls,
    fallbackExteriors,
    fallbackInteriors,
    thumbnailCount,
  } = calculateThumbnails({
    exteriorImageUrls,
    interiorImageUrls,
    interior360,
  });

  const exteriorSideThumbnail = exteriorThumbnailUrl
    ? createThumbnail(exteriorThumbnailUrl, 'Vehicle exterior side', onClickDelegate)
    : null;

  const backfilledExteriorThumbnails = fallbackExteriors.map((url, index) =>
    createThumbnail(url, `Vehicle exterior ${index + 1}`, onClickDelegate)
  );

  const interiorThumbnails = interiorThumbnailUrls.map((url, index) =>
    createThumbnail(url, `Vehicle interior ${index + 1}`, onClickDelegate)
  );

  const backfilledInteriorThumbnails = fallbackInteriors.map((url, index) =>
    createThumbnail(url, `Vehicle interior extra ${index + 1}`, onClickDelegate)
  );

  const interior360Thumbnail = interior360 ? (
    <Button
      variant="clear"
      onClick={() => onClickDelegate(interior360)}
      className="h-full w-full rounded-[16px] object-cover"
    >
      <motion.div
        className="group relative flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-[16px]"
        whileTap={{ scale: 0.98 }}
        transition={{ duration: 0.1 }}
      >
        <img
          src={
            // Preserve original preference order: index 2, then 1, then 0, then fallback to 360 image if it exists. 360 preview is not preferred but better than not showing anything.
            interiorThumbnailUrls[2] ??
            interiorThumbnailUrls[1] ??
            interiorThumbnailUrls[0] ??
            interior360
          }
          alt="Vehicle interior 360 preview"
          fetchPriority="high"
          loading="eager"
          className="absolute inset-0 z-0 h-full w-full blur-[4px] transition-all group-hover:blur-[2px]"
        />
        <p className="z-1 rounded-[32px] bg-flexcar-neutral100/90 px-[12px] py-[8px] font-sans-tight text-[10px] font-bold leading-[150%] tracking-[0.8px] text-flexcar-neutral20 group-hover:bg-flexcar-neutral100 group-hover:text-flexcar-neutral1">
          <span className="whitespace-nowrap">360º</span>{' '}
          <span className="hidden whitespace-nowrap min-[1300px]:inline">INTERIOR</span>
        </p>
      </motion.div>
    </Button>
  ) : null;

  const viewAllTile =
    thumbnailCount > 0 ? (
      <Button
        variant="clear"
        className="group flex aspect-[4/3] h-full w-full flex-row items-center justify-center gap-[4px] rounded-[16px] border-[1px] border-solid border-flexcar-neutral80 bg-flexcar-neutral100 transition-all min-[1300px]:flex-row"
        onClick={() => onClickDelegate()}
      >
        <Gallery className="h-[24px] w-[27px] group-hover:text-flexcar-blue40" />
        <p className="group-hover body3 hidden text-flexcar-neutral10 lg:button1 group-hover:text-flexcar-blue40 xl:block">
          View all
        </p>
      </Button>
    ) : null;

  /**
   * Order of thumbnails:
   * 1. Exterior thumbnail
   * 2. Exterior thumbnail fallbacks (if any)
   * 3. Interior thumbnails
   * 4. Interior thumbnail fallbacks (if any)
   * 5. 360 thumbnail
   * 6. View all tile
   */
  return compact(
    concat(
      exteriorSideThumbnail,
      backfilledExteriorThumbnails,
      interiorThumbnails,
      backfilledInteriorThumbnails,
      interior360Thumbnail,
      viewAllTile
    )
  );
}
